import { ActivateFocusInput, ExclusionReason, FocusMode, StudentTargetFocus } from '../models/UnitFocus';
import { SkillStatesKey } from '../models/SkillState';

export interface ProgressStudent {
  id: string;
  name: string;
  publicUserId: string;
}

export interface SkillProgressForUnit {
  skillId: string;
  buckets: { result: string; students: ProgressStudent[] }[];
}

interface ProgressEligibilityResult {
  included: ProgressStudent[];
  excluded: { student: ProgressStudent; reason: ExclusionReason }[];
}

const DEMONSTRATED_RESULTS = new Set<string>([SkillStatesKey.PASSED, SkillStatesKey.PLACED_OUT]);

export function resolveEligibilityForUnits(
  coreSkillIdsPerUnit: string[][],
  progress: SkillProgressForUnit[],
  roadmapMemberIds?: ReadonlySet<string>,
): ProgressEligibilityResult[] {
  const roster = new Map<string, ProgressStudent>();
  const demonstratedByStudent = new Map<string, Set<string>>();
  for (const sp of progress) {
    for (const bucket of sp.buckets) {
      const demonstrated = DEMONSTRATED_RESULTS.has(bucket.result);
      for (const student of bucket.students) {
        if (roadmapMemberIds && !roadmapMemberIds.has(student.id)) continue;
        if (!roster.has(student.id)) roster.set(student.id, student);
        if (!demonstrated) continue;
        const done = demonstratedByStudent.get(student.id) ?? new Set<string>();
        done.add(sp.skillId);
        demonstratedByStudent.set(student.id, done);
      }
    }
  }

  const students = [...roster.values()];
  return coreSkillIdsPerUnit.map(coreSkillIds => {
    const included: ProgressStudent[] = [];
    const excluded: { student: ProgressStudent; reason: ExclusionReason }[] = [];
    for (const student of students) {
      const done = demonstratedByStudent.get(student.id);
      const ahead = coreSkillIds.length > 0 && coreSkillIds.every(id => done?.has(id) ?? false);
      if (ahead) excluded.push({ student, reason: 'finished_this_unit' });
      else included.push(student);
    }
    return { included, excluded };
  });
}

export function resolveEligibilityFromProgress(
  coreSkillIds: string[],
  progress: SkillProgressForUnit[],
  roadmapMemberIds?: ReadonlySet<string>,
): ProgressEligibilityResult {
  return resolveEligibilityForUnits([coreSkillIds], progress, roadmapMemberIds)[0];
}

function endActiveRows(
  rows: StudentTargetFocus[],
  shouldEnd: (row: StudentTargetFocus) => boolean,
): StudentTargetFocus[] {
  return rows.map(row => (row.isActive && shouldEnd(row) ? { ...row, isActive: false } : row));
}

export function applyActivation(existing: StudentTargetFocus[], newRows: StudentTargetFocus[]): StudentTargetFocus[] {
  const incomingUsers = new Set(newRows.map(r => r.userId));
  const ended = endActiveRows(existing, r => incomingUsers.has(r.userId));
  return [...ended, ...newRows];
}

export function applyDeactivation(
  existing: StudentTargetFocus[],
  targetId: string,
  userIds?: string[],
): StudentTargetFocus[] {
  const ending = userIds && new Set(userIds);
  return endActiveRows(existing, row => row.targetId === targetId && (!ending || ending.has(row.userId)));
}

export function activateTarget(activeTargetIds: string[], targetId: string): string[] {
  return activeTargetIds.includes(targetId) ? activeTargetIds : [...activeTargetIds, targetId];
}

export function deactivateTarget(activeTargetIds: string[], targetId: string): string[] {
  return activeTargetIds.filter(id => id !== targetId);
}

export function classIdsWithFocusRecipients(
  classIds: string[],
  memberIdsByClass: ReadonlyMap<string, ReadonlySet<string>>,
  includedStudentIds: readonly string[],
): string[] {
  return classIds.filter(classId => {
    const members = memberIdsByClass.get(classId);
    return !!members && includedStudentIds.some(id => members.has(id));
  });
}

export function setTargetClassIds(
  classIds: Record<string, string[]>,
  targetId: string,
  ids: string[],
): Record<string, string[]> {
  return { ...classIds, [targetId]: ids };
}

export function clearTargetClassIds(classIds: Record<string, string[]>, targetId: string): Record<string, string[]> {
  if (!(targetId in classIds)) return classIds;
  const next = { ...classIds };
  delete next[targetId];
  return next;
}

interface StudentCurrentUnitNode {
  id: string;
  name?: string | null;
  summary?: string | null;
}

export function mapStudentCurrentUnit(
  node: StudentCurrentUnitNode | null | undefined,
): { id: string; name: string } | null {
  if (!node?.id) return null;
  const name = node.name?.trim();
  if (!name) return null;
  return { id: node.id, name };
}

interface CreateStudentTargetFocusInput {
  userId: string;
  targetId: string;
  otherTargetsBlocked: boolean;
}

export function buildCreateFocusInputs(
  userIds: string[],
  targetId: string,
  focusMode: FocusMode,
): CreateStudentTargetFocusInput[] {
  const otherTargetsBlocked = focusMode === 'restrict';
  return userIds.map(userId => ({ userId, targetId, otherTargetsBlocked }));
}

interface UpdateStudentTargetFocusInput {
  id: string;
  otherTargetsBlocked: boolean;
}

export function buildUpdateFocusInputs(
  focuses: StudentTargetFocus[],
  targetId: string,
  focusMode: FocusMode,
): UpdateStudentTargetFocusInput[] {
  const otherTargetsBlocked = focusMode === 'restrict';
  return focuses.filter(r => r.isActive && r.targetId === targetId).map(r => ({ id: r.id, otherTargetsBlocked }));
}

export function applyFocusSettingsUpdate(
  existing: StudentTargetFocus[],
  targetId: string,
  focusMode: FocusMode,
): StudentTargetFocus[] {
  return existing.map(r => (r.isActive && r.targetId === targetId ? { ...r, focusMode } : r));
}

export function mapPersistedFocusRows(
  rows: ReadonlyArray<{
    id: string;
    isActive: boolean;
    otherTargetsBlocked: boolean;
    target: { id: string } | null | undefined;
    user?: { id: string } | null;
  } | null>,
): StudentTargetFocus[] {
  return rows.flatMap(row =>
    row?.target?.id
      ? {
          id: row.id,
          userId: row.user?.id ?? '',
          targetId: row.target.id,
          isActive: row.isActive,
          focusMode: row.otherTargetsBlocked ? ('restrict' as const) : ('recommend' as const),
        }
      : [],
  );
}

export function buildActivatedFocusRows(
  nodes: ReadonlyArray<{ id: string; isActive: boolean } | null> | null | undefined,
  input: ActivateFocusInput,
): StudentTargetFocus[] {
  if (!nodes) return [];
  return nodes.flatMap((node, i) =>
    node
      ? {
          id: node.id,
          userId: input.userIds[i] ?? '',
          targetId: input.targetId,
          isActive: node.isActive,
          focusMode: input.focusMode,
        }
      : [],
  );
}

function getActiveFocusForTarget(focuses: StudentTargetFocus[], targetId: string): StudentTargetFocus | null {
  for (let i = focuses.length - 1; i >= 0; i--) {
    const r = focuses[i];
    if (r.isActive && r.targetId === targetId) return r;
  }
  return null;
}

export function resolveFocusRestrictDefault(focuses: StudentTargetFocus[], targetId: string): boolean {
  return (getActiveFocusForTarget(focuses, targetId)?.focusMode ?? 'restrict') === 'restrict';
}

export interface ActiveFocusUnit {
  targetId: string;
  roadmapId: string;
}

export function activeFocusUnits(
  rows: ReadonlyArray<{
    isActive: boolean;
    target: { id: string; targetGroup?: { id: string } | null } | null | undefined;
  } | null>,
): ActiveFocusUnit[] {
  const byTarget = new Map<string, ActiveFocusUnit>();
  for (const row of rows) {
    if (row?.isActive && row.target?.id && !byTarget.has(row.target.id)) {
      byTarget.set(row.target.id, { targetId: row.target.id, roadmapId: row.target.targetGroup?.id ?? '' });
    }
  }
  return [...byTarget.values()];
}

export function groupFocusClassIds(
  rows: ReadonlyArray<{
    isActive: boolean;
    targetId: string | null | undefined;
    classId: string | null | undefined;
  } | null>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const row of rows) {
    if (!row?.isActive || !row.targetId || !row.classId) continue;
    const list = (map[row.targetId] ??= []);
    if (!list.includes(row.classId)) list.push(row.classId);
  }
  return map;
}

export function resolveNextUnitId(
  orderedUnitIds: string[],
  fromTargetId: string,
  completedUnitIds?: ReadonlySet<string>,
): string {
  const fromIndex = orderedUnitIds.indexOf(fromTargetId);
  if (fromIndex === -1) return '';
  for (let i = fromIndex + 1; i < orderedUnitIds.length; i++) {
    const id = orderedUnitIds[i];
    if (!completedUnitIds || !completedUnitIds.has(id)) return id;
  }
  return '';
}

/** The active-focus shape isTargetBlocked needs: is blocking on, and which unit is the focus. */
export interface BlockingActiveFocus {
  otherTargetsBlocked: boolean;
  unit: { id: string };
}

/**
 * Whether a unit reads as "blocked" for the student on the All Units page (US-9).
 *
 * Blocking is a property of the student's ACTIVE FOCUS — the teacher's "Other units
 * blocked" setting (FocusMode 'restrict') — applied to every unit EXCEPT the focused
 * one. It is deliberately NOT tied to progress: a completed non-focus unit is still
 * blocked while the setting is on. Returns false when there is no active focus or the
 * focus is in recommend mode (blocking off).
 */
export function isTargetBlocked(unitId: string, activeFocus: BlockingActiveFocus | null | undefined): boolean {
  return !!activeFocus?.otherTargetsBlocked && activeFocus.unit.id !== unitId;
}
